import React, { Ref, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Flex from '@cobalt/react-flex';

import { treeToList } from './treeUtil';

import CascaderSelect from './cascader-select';

import { CascaderSelectOptionType } from './types';

export interface CascaderProps {
  value?: string[];
  defaultValue?: string[];
  options: CascaderSelectOptionType[];
  size?: 'small' | 'medium';
  variation?: 'error' | 'warning';
  disabled?: boolean;
  allowClear?: boolean;
  changeOnSelect?: boolean;
  transparent?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  className?: string;
  prefixCls?: string;
  forwardedRef?: Ref<HTMLInputElement>;
  noResultsFoundText?: string;
  backText?: string;
  onChange: (value: string[]) => void;
  onClear?: () => void;
  onBlur?: () => void;
}

const cls = 'cascader';

const Cascader: React.FC<CascaderProps> = (props) => {
  const [t] = useTranslation();

  const {
    value: values,
    defaultValue,
    options,
    variation,
    size,
    disabled,
    allowClear = true,
    changeOnSelect = false,
    transparent = false,
    placeholder = t('common.select_one', { defaultValue: 'Select one' }),
    style,
    className,
    prefixCls = 'cobalt',
    noResultsFoundText = t('common.no_results_found', {
      defaultValue: 'No results found',
    }),
    backText = t('common.back', { default: 'Back' }),
    onChange,
    onBlur,
  } = props;

  const [tempVals, setTempVals] = useState(values || defaultValue || []);
  const componentCls = `${prefixCls}-${cls}`;
  const mergeClassNames = classNames(`${componentCls}`, className);

  const optionList = useMemo(() => treeToList(options), [options]);

  const virtualValues = useMemo(
    () => (values ? optionList.filter((item) => values.indexOf(item.value) > -1).map((item) => item.value) : []),
    [values, optionList],
  );

  const virtualTempVals = useMemo(
    () => (tempVals ? optionList.filter((item) => tempVals.indexOf(item.value) > -1).map((item) => item.value) : []),
    [tempVals, optionList],
  );

  useEffect(() => {
    if (values === undefined) {
      setTempVals([]);
    } else if (values.length > 0) {
      setTempVals(values);
    }
  }, [values]);

  const handleSelect = (value: string, level?: number, isLastLevel?: boolean) => {
    if (level === -1 && !changeOnSelect && (!values || (Array.isArray(values) && values.length === 0))) {
      setTempVals([]);
      onChange([]);
    } else if (level === -1 && !changeOnSelect && Array.isArray(values) && values.length > 0) {
      // Keep the cascader temporary values in sync with the original values when you click on the other drop down
      setTempVals([...virtualValues]);
    } else if (level !== -1) {
      const copyValues = [...virtualTempVals];
      copyValues[level! - 1] = value;
      const realValue = copyValues.filter((item) => item); //Filters out empty strings that fall back
      setTempVals(realValue);
      if (isLastLevel || changeOnSelect) {
        onChange(realValue);
      }
    }
  };

  return (
    <Flex data-testid="cascader" className={mergeClassNames} style={style} direction="column">
      <Flex width="100%">
        <CascaderSelect
          style={{ width: '100%' }}
          contentStyle={{ overflowX: 'hidden' }}
          size={size}
          disabled={disabled}
          placeholder={placeholder}
          value={virtualValues}
          tempVals={virtualTempVals}
          changeOnSelect={changeOnSelect}
          transparent={transparent}
          options={optionList}
          onSelect={handleSelect}
          variation={variation}
          allowClear={allowClear}
          noResultsFoundText={noResultsFoundText}
          backText={backText}
          onClear={() => {
            setTempVals([]);
            onChange([]);
          }}
          onBlur={onBlur}
        />
      </Flex>
    </Flex>
  );
};

export default Cascader;
